import React from 'react';
import { DAY_MS } from '../../utils/dates';

/* Izvor aktivnosti (dbo.AktivnostIzvor, polja reda: izvor, listicId, listic = naziv fajla listića):
     KARTICA / VOZILO — digitalni zapis, ZAKLJUČAN (ne menja se i ne briše), osim aktivnosti "nepoznato"
     LISTIC           — analogni listić (zamenjuje se ceo novim listićem)
     RUCNO            — ručni unos / izmena
     bez izvora       — uneto pre evidencije izvora (stara aplikacija, dan dopunjen odmorom; popuna 2026-11-04 označila je samo kartice) = ručni unos */
export const IZVORI = {
    KARTICA: { label: 'kartica', cls: 'kartica', ikona: 'pi pi-id-card' },
    VOZILO: { label: 'tahograf vozila', cls: 'kartica', ikona: 'pi pi-truck' },
    LISTIC: { label: 'listić', cls: 'listic', ikona: 'pi pi-stopwatch' },
    RUCNO: { label: 'ručno', cls: 'rucno', ikona: 'pi pi-pencil' },
};
const RANIJE = { label: 'ručno', cls: 'rucno', ikona: 'pi pi-pencil' };

/** red sa kartice / tahografa vozila (osim odmora i nepoznatog) se ne menja — isto pravilo proverava i baza (dbo.izvorAktivnostiDana);
    odmor i nepoznato sa kartice smeju se brisati i listić ih popunjava */
export const slobodanTip = (t) => t === 'break/rest' || t === 'unknown';
export const zakljucan = (d) => !!d && (d.zakljucan === true || ((d.izvor === 'KARTICA' || d.izvor === 'VOZILO') && !slobodanTip(d.activityType)));

/** ključ izvora (redovi istog izvora se smeju spajati) */
export const izvorKljuc = (d) => `${(d && d.izvor) || 'RUCNO'}|${(d && d.listicId) || ''}`;

export const izvorInfo = (izvor) => IZVORI[izvor] || RANIJE;

/** naziv listića bez putanje i ekstenzije */
const kratakNaziv = (n) => String(n || '').split(/[\\/]/).pop().replace(/\.[a-z0-9]+$/i, '');

/** oznaka izvora reda (tabela aktivnosti); listić nosi i naziv fajla */
export function IzvorOznaka({ d, sufiks }) {
    const info = izvorInfo(d && d.izvor);
    const listic = d && d.izvor === 'LISTIC' ? kratakNaziv(d.listic) : '';
    const title = d && d.izvor === 'LISTIC' ? `Listić ${d.listic || ''}`
        : d && d.izvor ? `Izvor: ${info.label}${zakljucan(d) ? ' — ne menja se' : ''}` : 'Ručni unos (uneto pre evidencije izvora)';
    return (
        <span className={`izvor ${info.cls}`} title={title}>
            <i className={info.ikona} aria-hidden="true" />
            {info.label}{listic && <span className="izvor-listic">· {listic}</span>}{sufiks}
        </span>
    );
}

/** traka izvora (ispod trake dana): delovi {od, do (ms od početka dana), izvor, ovaj (deo novog listića), kljuc, naziv};
    onKlik(kljuc): deo trake je dugme (Aktivnosti: izbor svih redova tog izvora za brisanje) */
export function TrakaIzvora({ delovi, onKlik }) {
    const pct = (ms) => `${(ms / DAY_MS) * 100}%`;
    return (
        <div className={`dan-izvor-bar${onKlik ? ' klik' : ''}`} aria-hidden={onKlik ? undefined : 'true'}>
            {delovi.filter((x) => x.do > x.od).map((x, i) => {
                const cls = `izv-seg ${x.ovaj ? 'listic ovaj' : izvorInfo(x.izvor).cls}`;
                const style = { left: pct(x.od), width: pct(x.do - x.od) };
                return onKlik && x.kljuc
                    ? <button key={i} type="button" className={cls} style={style} onClick={() => onKlik(x.kljuc)}
                        title={`${x.naziv || izvorInfo(x.izvor).label} — izaberi za brisanje`} aria-label={`${x.naziv || izvorInfo(x.izvor).label} — izaberi za brisanje`} />
                    : <span key={i} className={cls} style={style} />;
            })}
        </div>
    );
}

export function LegendaIzvora({ ovaj = false }) {
    return (
        <div className="izv-legenda" aria-label="Legenda izvora">
            <span><i className="izv-seg kartica" /> kartica / tahograf vozila (ne menja se)</span>
            <span><i className="izv-seg listic" /> listić</span>
            {ovaj && <span><i className="izv-seg listic ovaj" /> ovaj listić</span>}
            <span><i className="izv-seg rucno" /> ručno</span>
        </div>
    );
}

const hhmm = (v) => { const x = new Date(v); return `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`; };
const kraj = (v, dan) => { const x = new Date(v); return x.getTime() - dan >= DAY_MS ? '24:00' : hhmm(x); };

/** grupe izvora dana (redovi u zidnom vremenu): kartica / tahograf vozila, svaki listić posebno ("Traka 1", "Traka 2" po redu
    unosa u danu), ručno, ranije; od–do = prvi početak i poslednji kraj grupe (odmor se ne broji kad grupa ima i drugih aktivnosti) */
export function grupeIzvora(details, day) {
    const d0 = new Date(day);
    const dan = new Date(d0.getFullYear(), d0.getMonth(), d0.getDate()).getTime();
    const po = new Map();
    [...(details || [])].sort((a, b) => new Date(a.startTime) - new Date(b.startTime)).forEach((r) => {
        const k = izvorKljuc(r);
        if (!po.has(k)) po.set(k, { kljuc: k, izvor: r.izvor || 'RUCNO', listicId: r.listicId || null, listic: r.listic || null, redovi: [] });
        po.get(k).redovi.push(r);
    });
    let traka = 0;
    const red = { KARTICA: 0, VOZILO: 1, LISTIC: 2, RUCNO: 3 };
    return [...po.values()].map((g) => {
        const rad = g.redovi.filter((r) => r.activityType !== 'break/rest');
        const opseg = rad.length ? rad : g.redovi;
        const od = Math.min(...opseg.map((r) => new Date(r.startTime).getTime()));
        const doo = Math.max(...opseg.map((r) => new Date(r.endTime).getTime()));
        return { ...g, od, do: doo, vreme: `${hhmm(od)}–${kraj(doo, dan)}`, brisivo: g.redovi.some((r) => !zakljucan(r)) };
    }).sort((a, b) => (red[a.izvor] ?? 4) - (red[b.izvor] ?? 4) || a.od - b.od)
        .map((g) => ({ ...g, naziv: g.izvor === 'LISTIC' ? `Traka ${++traka}` : izvorInfo(g.izvor).label.replace(/^./, (c) => c.toUpperCase()) }));
}

/** dugmad grupa izvora dana iznad tabele: klik izabere sve redove grupe za brisanje (redovi sa kartice se ne biraju) */
export function IzvoriDana({ grupe, onIzaberi }) {
    if (!grupe.length) return null;
    return (
        <div className="izvori-dana" role="group" aria-label="Izvori aktivnosti dana">
            {grupe.map((g) => {
                const info = izvorInfo(g.izvor);
                const naslov = g.izvor === 'LISTIC' ? `${g.naziv} — listić ${g.listic || ''}` : g.naziv;
                const sadrzaj = (
                    <>
                        <i className={info.ikona} aria-hidden="true" />
                        <b>{g.naziv}</b>
                        {g.izvor === 'LISTIC' && g.listic && <span className="izvor-listic">{kratakNaziv(g.listic)}</span>}
                        <span className="num">{g.vreme}</span>
                    </>
                );
                return onIzaberi && g.brisivo ? (
                    <button key={g.kljuc} type="button" className={`izvor-grupa ${info.cls}`} onClick={() => onIzaberi(g)}
                        title={`${naslov}: izaberi sve aktivnosti za brisanje`} aria-label={`${naslov} ${g.vreme} — izaberi za brisanje`}>
                        {sadrzaj}<i className="pi pi-check-square izvor-grupa-izbor" aria-hidden="true" />
                    </button>
                ) : (
                    <span key={g.kljuc} className={`izvor-grupa ${info.cls}`} title={zakljucan(g.redovi[0]) ? `${naslov} — ne menja se` : naslov}>
                        {sadrzaj}{zakljucan(g.redovi[0]) && <i className="pi pi-lock" aria-hidden="true" />}
                    </span>
                );
            })}
        </div>
    );
}
